import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Drawer,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { RootState, AppDispatch } from '../store';
import { fetchFileById, deleteFile, toggleFileStarred } from '../store/slices/filesSlice';
import { FileItem } from '../types/files';
import { useSnackbar } from 'notistack';
import VersionHistory from '../components/file/VersionHistory';
import { FileLock } from '../components/file/FileLock';
import AdvancedMetadataEditor from '../components/file/AdvancedMetadataEditor';
import FileLockDialog from '../components/file/FileLockDialog';
import usePageTitle from '../hooks/usePageTitle';
import fileService from '../services/fileService';
import PreviewEngine, { PreviewEngineHandle } from '../preview/PreviewEngine';
import PreviewToolbar from '../components/preview/PreviewToolbar';
import ShareDialog from '../components/share/ShareDialog';
import { formatFileSize } from '../utils/formatters';
import {
  getPreviewMimeType,
  getPreviewCategory,
  extensionOf,
  getFriendlyFileType,
  getOpenedWithApp,
  type PreviewCategory,
} from '../utils/previewUtils';
import { isDocxFile, openFileInDocs } from '../utils/docsIntegration';
import { isSpreadsheetFile, openFileInGrid } from '../utils/gridIntegration';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TableChartIcon from '@mui/icons-material/TableChart';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`preview-tabpanel-${index}`}
      aria-labelledby={`preview-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

const FilePreview: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folderId');
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentFile, loading, error } = useSelector((state: RootState) => state.files);
  const [infoOpen, setInfoOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [folderFiles, setFolderFiles] = useState<FileItem[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const engineRef = useRef<PreviewEngineHandle>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const [category, setCategory] = useState<PreviewCategory>('unsupported');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textFontSize, setTextFontSize] = useState(14);
  const [textWordWrap, setTextWordWrap] = useState(true);

  usePageTitle(currentFile?.name ? `${currentFile.name} — Preview` : 'File Preview');

  useEffect(() => {
    if (fileId) dispatch(fetchFileById(fileId));
  }, [dispatch, fileId]);

  useEffect(() => {
    if (!folderId || !fileId) {
      setFolderFiles([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const fid = folderId === 'root' ? 'root' : folderId;
        const { items } = await fileService.getFolderContents(fid, { limit: 1000 });
        if (cancelled) return;
        setFolderFiles((items as FileItem[]).filter((i) => i.type === 'file'));
      } catch {
        if (!cancelled) setFolderFiles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folderId, fileId]);

  useEffect(() => {
    setImageZoom(1);
    setImageRotation(0);
    setPdfScale(1);
  }, [fileId]);

  const handleDownload = async () => {
    if (!fileId) return;
    try {
      enqueueSnackbar('Downloading file...', { variant: 'info' });
      await fileService.downloadFile(fileId);
      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    }
  };

  const handlePrint = async () => {
    if (!fileId) return;
    try {
      const url = mediaUrl || (await fileService.buildPreviewUrl(fileId, undefined, { mode: 'preview', optimize: true }, { withAccessToken: true }));
      const w = window.open(url, '_blank');
      if (w) w.onload = () => w.print();
      else throw new Error('Pop-up blocked');
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Print failed', { variant: 'error' });
    }
  };

  const handleStarToggle = () => {
    if (currentFile) dispatch(toggleFileStarred(currentFile.id));
  };

  const handleDelete = async () => {
    if (!fileId || !window.confirm('Are you sure you want to delete this file?')) return;
    await dispatch(deleteFile(fileId) as never);
    navigate(-1);
  };

  const handleBackClick = () => navigate(-1);

  const previewNavIndex = currentFile ? folderFiles.findIndex((f) => f.id === currentFile.id) : -1;

  const goPrevPreview = useCallback(() => {
    if (previewNavIndex <= 0 || !folderId) return;
    navigate(`/preview/${folderFiles[previewNavIndex - 1].id}?folderId=${encodeURIComponent(folderId)}`);
  }, [previewNavIndex, folderId, folderFiles, navigate]);

  const goNextPreview = useCallback(() => {
    if (previewNavIndex < 0 || previewNavIndex >= folderFiles.length - 1 || !folderId) return;
    navigate(`/preview/${folderFiles[previewNavIndex + 1].id}?folderId=${encodeURIComponent(folderId)}`);
  }, [previewNavIndex, folderId, folderFiles, navigate]);

  const toggleFullscreen = useCallback(() => {
    const el = previewContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (infoOpen) return;
      if (e.key === 'ArrowLeft') goPrevPreview();
      if (e.key === 'ArrowRight') goNextPreview();
      if (e.key === 'Escape' && document.fullscreenElement) document.exitFullscreen();
      if (category === 'image') {
        if (e.key === '+' || e.key === '=') setImageZoom((z) => Math.min(3, z + 0.2));
        if (e.key === '-') setImageZoom((z) => Math.max(0.5, z - 0.2));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrevPreview, goNextPreview, category, infoOpen]);

  if (loading && !currentFile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>Go Back</Button>
      </Container>
    );
  }

  if (!currentFile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>File not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>Go Back</Button>
      </Container>
    );
  }

  const mime = getPreviewMimeType(currentFile);
  const resolvedCategory = category !== 'unsupported' ? category : getPreviewCategory(mime, extensionOf(currentFile.name));
  const openedWithApp = getOpenedWithApp(currentFile.name, mime);
  const navProps =
    folderId && folderFiles.length > 1 && previewNavIndex >= 0
      ? {
          index: previewNavIndex,
          total: folderFiles.length,
          onPrev: goPrevPreview,
          onNext: goNextPreview,
          canPrev: previewNavIndex > 0,
          canNext: previewNavIndex < folderFiles.length - 1,
        }
      : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px - 48px)',
        flex: 1,
      }}
    >
      <Paper sx={{ px: 2, py: 1, borderRadius: 0 }} elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" minWidth={0}>
            <Box minWidth={0}>
              <Typography variant="h6" noWrap sx={{ maxWidth: { xs: 220, md: 480 } }}>
                {currentFile.name}
              </Typography>
              {navProps && (
                <Typography variant="caption" color="text.secondary">
                  {navProps.index + 1} of {navProps.total}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            {isDocxFile(currentFile.name, mime) && (
              <Tooltip title="Open in Docs">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EditNoteIcon />}
                  onClick={() => openFileInDocs(currentFile.id)}
                  sx={{ mr: 1 }}
                >
                  Open in Docs
                </Button>
              </Tooltip>
            )}
            {isSpreadsheetFile(currentFile.name, mime) && (
              <Tooltip title="Open in Grid">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<TableChartIcon />}
                  onClick={() => openFileInGrid(currentFile.id)}
                  sx={{ mr: 1 }}
                >
                  Open in Grid
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Share">
              <IconButton onClick={() => setShareOpen(true)} aria-label="Share">
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions">
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { handleStarToggle(); setMenuAnchor(null); }}>
                <ListItemIcon>{currentFile.isStarred ? <StarIcon color="warning" /> : <StarBorderIcon />}</ListItemIcon>
                <ListItemText>{currentFile.isStarred ? 'Unstar' : 'Star'}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setInfoOpen(true); setMenuAnchor(null); }}>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText>File information</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleDelete(); setMenuAnchor(null); }}>
                <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <PreviewToolbar
        variant="full"
        category={resolvedCategory}
        fileName={currentFile.name}
        imageZoom={imageZoom}
        imageRotation={imageRotation}
        pdfScale={pdfScale}
        isFullscreen={isFullscreen}
        textWordWrap={textWordWrap}
        textFontSize={textFontSize}
        nav={navProps}
        onImageZoomChange={setImageZoom}
        onImageRotationChange={setImageRotation}
        onImageReset={() => { setImageZoom(1); setImageRotation(0); }}
        onPdfScaleChange={setPdfScale}
        onPdfFit={() => setPdfScale(1)}
        onFullscreenToggle={toggleFullscreen}
        showDownload={resolvedCategory !== 'office' && resolvedCategory !== 'unsupported'}
        onDownload={handleDownload}
        onPrint={handlePrint}
        onOpenNewTab={mediaUrl ? () => window.open(mediaUrl, '_blank') : undefined}
        onCopyText={() => {
          const text = engineRef.current?.getTextContent();
          if (text) navigator.clipboard.writeText(text);
        }}
        onTextWordWrapToggle={() => setTextWordWrap((w) => !w)}
        onTextFontSizeChange={setTextFontSize}
        onRetry={() => engineRef.current?.retry()}
      />

      <Box
        ref={previewContainerRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <PreviewEngine
          ref={engineRef}
          file={currentFile}
          imageZoom={imageZoom}
          imageRotation={imageRotation}
          pdfScale={pdfScale}
          textFontSize={textFontSize}
          textWordWrap={textWordWrap}
          onCategoryChange={setCategory}
          onMediaUrlChange={setMediaUrl}
        />
      </Box>

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        itemId={currentFile.id}
        itemName={currentFile.name}
        itemType="file"
        currentShares={currentFile.sharedWith as never[] | undefined}
      />

      <Drawer anchor="right" open={infoOpen} onClose={() => setInfoOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1 }} />
          <Typography variant="h6">File information</Typography>
        </Box>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<InfoIcon />} label="Details" />
          <Tab icon={<VisibilityIcon />} label="Activity" />
          <Tab icon={<ShareIcon />} label="Sharing" />
          <Tab icon={<HistoryIcon />} label="Versions" />
          <Tab icon={<LockIcon />} label="Locking" />
          <Tab icon={<DescriptionIcon />} label="Metadata" />
        </Tabs>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body2">{currentFile.name}</Typography>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Typography variant="body2">{getFriendlyFileType(currentFile.name, mime)}</Typography>
              <Typography variant="body2" color="text.secondary">Size</Typography>
              <Typography variant="body2">{formatFileSize(currentFile.size)}</Typography>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography variant="body2">{formatDate(currentFile.createdAt)}</Typography>
              <Typography variant="body2" color="text.secondary">Modified</Typography>
              <Typography variant="body2">{formatDate(currentFile.updatedAt)}</Typography>
              <Typography variant="body2" color="text.secondary">Owner</Typography>
              <Typography variant="body2">{currentFile.ownerName}</Typography>
              {openedWithApp && (
                <>
                  <Typography variant="body2" color="text.secondary">Opened with</Typography>
                  <Typography variant="body2">{openedWithApp}</Typography>
                </>
              )}
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {currentFile.activity?.length ? (
              currentFile.activity.map((activity, index) => (
                <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">{activity.action}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activity.userName} · {formatDate(activity.timestamp)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">No activity to display</Typography>
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {currentFile.sharedWith?.length ? (
              currentFile.sharedWith.map((share, index) => (
                <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">{share.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {share.permission} · {formatDate(share.sharedAt)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">Not shared</Typography>
            )}
            <Button variant="outlined" startIcon={<ShareIcon />} onClick={() => setShareOpen(true)} sx={{ mt: 2 }}>
              Share
            </Button>
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <VersionHistory
              fileId={currentFile.id}
              onVersionRestored={() => {
                dispatch(fetchFileById(currentFile.id));
                enqueueSnackbar('File version restored', { variant: 'success' });
              }}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <FileLock
              fileId={currentFile.id}
              onLockChange={(status) => {
                enqueueSnackbar(`File ${status === 'locked' ? 'locked' : 'unlocked'}`, { variant: 'success' });
                dispatch(fetchFileById(currentFile.id));
              }}
            />
            <Button variant="outlined" startIcon={<LockIcon />} sx={{ mt: 2 }} onClick={() => setLockDialogOpen(true)}>
              Manage lock
            </Button>
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <AdvancedMetadataEditor
              fileId={currentFile.id}
              fileName={currentFile.name}
              metadata={currentFile.metadata || {}}
              onChange={() => {
                dispatch(fetchFileById(currentFile.id));
                enqueueSnackbar('Metadata updated', { variant: 'success' });
              }}
            />
          </TabPanel>
        </Box>
      </Drawer>

      <FileLockDialog
        open={lockDialogOpen}
        onClose={() => setLockDialogOpen(false)}
        fileId={currentFile.id}
        fileName={currentFile.name}
        isLocked={currentFile.isLocked || false}
        lockedBy={currentFile.lockedBy || 'unknown'}
        lockedByName={currentFile.lockedByName || 'Unknown User'}
        lockedAt={currentFile.lockedAt}
        lockedUntil={currentFile.lockExpiresAt}
        onLockStatusChange={() => dispatch(fetchFileById(currentFile.id))}
      />
    </Box>
  );
};

export default FilePreview;
